import { useMutation } from "@tanstack/react-query";
import { AlertCircle, RefreshCw } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Modal } from "@/components/ui/dialog";
import { Field, Form, Input } from "@/components/ui/field";
import { FkSelect } from "@/components/ui/fk-select";
import { OptionSelect } from "@/components/ui/option-select";
import { api, errorMessage, ifMatch, statusOf } from "@/services/api";
import { useTranslation } from "@/store/locale";

/** The footer's Submit button reaches the form across the modal by this name. */
const FORM_ID = "employee-form";

/** An employee is a user. */
export interface Employee {
  id: number;
  user_name: string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  full_name: string;
  avatar?: string | null;
  email_address: string | null;
  status: "active" | "password_expired" | "account_locked" | "inactive" | "deleted";
  is_superuser: boolean;
  is_password_request: boolean;
  club_id: number | null;
  manager_id: number | null;
  club: { id: number; club_name: string; club_code?: string } | null;
  is_temporary_club: boolean;
  temporary_club: { id: number; club_name: string; club_code?: string } | null;
  temporary_club_reason: string | null;
  temporary_start_date: string | null;
  temporary_end_date: string | null;
  has_active_temporary_club: boolean;
  groups: { id: number; name: string }[];
  request_reason?: string | null;
  password_request_at?: string | null;
  force_password_change?: boolean;
  last_password_change_at?: string | null;
  last_login_at?: string | null;
  created_by_id: number | null;
  updated_by_id: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * The rank that reports to a manager instead of holding an address.
 *
 * It decides two things at once, because they are one question: an associate
 * is reached through their manager, so the Manager field is theirs and the
 * Email field is everyone else's. "Clerk" is the same rank under an older
 * name and is treated identically.
 */
const isAssociateRoleName = (name: string) => /\b(associate|clerk)\b/i.test(name);

/**
 * The role an associate reports to, by name.
 *
 * One of the roles the product ships with, so the name is fixed rather than
 * configuration — the same way the rank above is recognised by name here.
 */
const SUPERVISOR_ROLE = "Supervisor";

/**
 * Long enough to outlast a fast typist finishing the number, short enough that
 * it does not read as a stuck field.
 */
const LOOKUP_DEBOUNCE_MS = 500;

/**
 * Why a posting is being recorded. Two, fixed.
 *
 * Not drawn from the configurable reasons table: these name the two kinds of
 * change the activity log records, so the list is the product's, not an
 * administrator's, and adding to it would mean a log entry nothing can file.
 */
const CLUB_CHANGE_REASONS = [
  { value: "Change Club Location", label: "Change Club Location" },
  { value: "Change Role Access", label: "Change Role Access" },
] as const;

const PASSWORD_LENGTH = 12;
/** Ambiguous glyphs left out — these get read down a phone line. */
const PASSWORD_SETS = [
  "abcdefghjkmnpqrstuvwxyz",
  "ABCDEFGHJKLMNPQRSTUVWXYZ",
  "23456789",
  "!@#$%&*",
];

/**
 * Twelve characters holding one of each class, then shuffled — so the mix is
 * guaranteed rather than left to luck, which is what makes it safe to check
 * only the length afterwards.
 */
function generatePassword(): string {
  const pick = (chars: string) =>
    chars[crypto.getRandomValues(new Uint32Array(1))[0]! % chars.length]!;
  const all = PASSWORD_SETS.join("");
  const filler = PASSWORD_LENGTH - PASSWORD_SETS.length;
  const chars = [
    ...PASSWORD_SETS.map(pick),
    ...Array.from({ length: filler }, () => pick(all)),
  ];
  for (let i = chars.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0]! % (i + 1);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.join("");
}

/**
 * The form asks for one name and the API stores three, so the split happens
 * here: first word and last word are the parts the column cannot be without,
 * and anything in between is the middle name. Nothing is stripped — a name is
 * whatever its owner says it is, apostrophes, hyphens and accents included.
 */
/**
 * A typed name as the three columns behind it.
 *
 * One word is a name: some people have a single one, and the field used to
 * refuse them with a complaint about a box that was filled in. The given name
 * is what is required; the rest is whatever else was typed, the last word
 * being the family name when there is more than one.
 */
function splitName(
  name: string,
): { first: string; middle: string | null; last: string | null } | null {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return { first: parts[0]!, middle: null, last: null };
  return {
    first: parts[0]!,
    middle: parts.slice(1, -1).join(" ") || null,
    last: parts[parts.length - 1]!,
  };
}

const joinName = (employee: Employee | null) =>
  employee
    ? [employee.first_name, employee.middle_name, employee.last_name].filter(Boolean).join(" ")
    : "";

interface Values {
  user_name: string;
  name: string;
  group_id: string;
  club_id: string;
  manager_id: string;
  email_address: string;
  password: string;
}

/** The temporary posting, which only an edit asks about. */
interface Posting {
  temporary_club_id: string;
  temporary_club_reason: string;
  temporary_start_date: string;
  temporary_end_date: string;
}

/** One message per field, keyed by the value it belongs to. */
type Errors = Partial<Record<keyof Values | keyof Posting, string>>;

/** What the directory answers with when it holds the employee number. */
interface SnrEmployee {
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  email_address: string | null;
  position: string | null;
  role: string | null;
  role_id: number | null;
}

export function EmployeeForm({
  employee,
  onClose,
  onSaved,
}: {
  employee: Employee | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslation();
  const creating = employee === null;

  const [values, setValues] = useState<Values>({
    user_name: employee?.user_name ?? "",
    name: joinName(employee),
    group_id: employee?.groups[0]?.id.toString() ?? "",
    club_id: employee?.club_id?.toString() ?? "",
    manager_id: employee?.manager_id?.toString() ?? "",
    // An address stored against an associate is not one of theirs: the role has
    // none, so whatever is on the row was put there by something that should
    // not have. It is not carried into the field when the role is changed to
    // one that does have an address — that address has to be typed.
    email_address: isAssociateRoleName(employee?.groups[0]?.name ?? "")
      ? ""
      : (employee?.email_address ?? ""),
    password: "",
  });
  const [posting, setPosting] = useState(employee?.is_temporary_club ?? false);
  /** Which end of a crossed pair of dates to complain about. See `setPosted`. */
  const [crossedOn, setCrossedOn] = useState<"temporary_start_date" | "temporary_end_date">(
    "temporary_start_date",
  );
  const [temp, setTemp] = useState<Posting>({
    temporary_club_id: employee?.temporary_club?.id.toString() ?? "",
    temporary_club_reason: employee?.temporary_club_reason ?? "",
    temporary_start_date: employee?.temporary_start_date ?? "",
    temporary_end_date: employee?.temporary_end_date ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const [roleName, setRoleName] = useState(employee?.groups[0]?.name ?? "");

  // The two halves of one question: an associate reports to a manager, and
  // every other role is reached at an address of their own. Neither shows
  // until a role has been picked, because until then neither applies.
  const managed = isAssociateRoleName(roleName);
  const showManager = roleName !== "" && managed;
  const showEmail = roleName !== "" && !managed;

  // Nothing is cleared by hand any more: `rules` below is read off the values
  // themselves, so a message cannot outlive the thing it is about.
  const set = (field: keyof Values, value: string) =>
    setValues((v) => ({ ...v, [field]: value }));

  /*
   * ---- The employee number, on Add ------------------------------------
   *
   * The number is the key into the directory, and it is also the one thing
   * that can already be taken. Both questions go to `/snr/{number}`, which
   * refuses with 409 when an account already holds the number and otherwise
   * answers with what HR has — one round trip rather than two racing ones.
   *
   * Everything the last answer left behind is retired on the way in: a stale
   * name locked to a number nobody is entering any more is worse than an
   * empty field.
   */
  const [lookup, setLookup] = useState<{ busy: boolean; note: string | null }>({
    busy: false,
    note: null,
  });
  const [nameLocked, setNameLocked] = useState(false);
  const [duplicate, setDuplicate] = useState<string | null>(null);
  /** What the last lookup wrote, so only its own leftovers get cleared. */
  const filled = useRef<{ name: string; email_address: string; group_id: string } | null>(null);
  /** Read by the debounce effect without making it re-run. */
  const runLookup = useRef<(number: string, signal: AbortSignal) => Promise<void>>(
    async () => {},
  );

  const askDirectory = async (number: string, signal: AbortSignal) => {
    setLookup({ busy: true, note: null });
    try {
      const found = (
        await api.get<SnrEmployee>(`/snr/${encodeURIComponent(number)}`, { signal })
      ).data;
      if (signal.aborted) return;
      const name = [found.first_name, found.middle_name, found.last_name]
        .filter(Boolean)
        .join(" ");
      const email = found.email_address ?? "";
      const group = found.role_id ? String(found.role_id) : "";
      filled.current = { name, email_address: email, group_id: group };
      setValues((v) => ({
        ...v,
        name: name || v.name,
        email_address: email || v.email_address,
        group_id: group || v.group_id,
      }));
      if (found.role) setRoleName(found.role);
      // Only the name is locked: the directory is authoritative about who the
      // number belongs to, and about nothing else on the form.
      setNameLocked(name !== "");
      setLookup({
        busy: false,
        note: found.role_id
          ? t("emp.foundRole")
              .replace("{position}", found.position ?? "—")
              .replace("{role}", found.role ?? "")
          : t("emp.foundNoRole").replace("{position}", found.position ?? "—"),
      });
    } catch (err) {
      if (signal.aborted) return;
      if (statusOf(err) === 409) {
        setLookup({ busy: false, note: null });
        setDuplicate(errorMessage(err, t("emp.duplicateNumber")));
        return;
      }
      // An outage at the directory must not stop an account being created by
      // hand, so this is a note rather than an error.
      setLookup({
        busy: false,
        note: `${errorMessage(err, t("emp.snrFailed"))} ${t("emp.fillByHand")}`,
      });
    }
  };

  // Kept current after every commit, so the debounce below can call the
  // latest closure without listing `t` and friends among its dependencies —
  // which would restart the timer on an unrelated re-render.
  useEffect(() => {
    runLookup.current = askDirectory;
  });

  /**
   * What typing in the number retires: the fields the last answer filled (but
   * never what was typed over them), the name lock, and the duplicate error.
   *
   * It belongs on the keystroke rather than in an effect — it is caused by the
   * edit, not by synchronising with anything — which also means the fields are
   * cleared in the same render as the character that cleared them.
   */
  const changeNumber = (raw: string) => {
    // Taken as typed. An employee number is an identifier rather than a
    // quantity — "EMP-00002" is as valid as "4021" — so nothing is filtered
    // out of it on the way in; the trim at submit and at lookup is the whole
    // of the tidying it gets.
    const previous = filled.current;
    filled.current = null;
    setValues((v) => ({
      ...v,
      user_name: raw,
      ...(previous
        ? {
            name: v.name === previous.name ? "" : v.name,
            email_address: v.email_address === previous.email_address ? "" : v.email_address,
            group_id: v.group_id === previous.group_id ? "" : v.group_id,
          }
        : {}),
    }));
    if (previous?.group_id) setRoleName("");
    setNameLocked(false);
    setLookup({ busy: false, note: null });
    setDuplicate(null);
  };

  useEffect(() => {
    // An edit never looks up: the number is fixed, and asking would only
    // report the account being edited as a duplicate of itself.
    const number = values.user_name.trim();
    if (!creating || !number) return;

    // Ask once the typing settles. A further keystroke or an unmount aborts
    // the request, so a late answer for an older number is dropped rather
    // than racing the current one into the fields.
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void runLookup.current(number, controller.signal);
    }, LOOKUP_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [values.user_name, creating]);

  /**
   * The address that follows the role.
   *
   * A role that hides the field has no address at all — not a blank one, and
   * not a synthetic one built from the employee number. An associate is
   * reached through their supervisor: their password goes to the supervisor's
   * address, which is where the server sends it, and an address of their own
   * would be one nobody reads and one more thing to keep unique.
   */
  const emailToSend = () => (showEmail ? values.email_address.trim() || null : null);

  /*
   * ---- The posting's dates --------------------------------------------
   *
   * Compared as the calendar days they are: `DatePicker` hands back
   * YYYY-MM-DD whatever it shows, and that sorts as it reads, so no clock,
   * timezone or parse is involved. Changing either date re-checks the pair
   * and retires the other's message, so only the date just touched carries
   * the complaint.
   */
  const crossed = (next: Posting) =>
    next.temporary_start_date !== "" &&
    next.temporary_end_date !== "" &&
    next.temporary_start_date > next.temporary_end_date;

  /**
   * Switching the block off retires its messages with it: no date check runs
   * while the toggle is off, so a complaint left over from when it was on
   * would be about fields nobody can see, and would block a save nobody could
   * fix. The values themselves stay put, so switching back on does not throw
   * away what was already filled in.
   */
  const togglePosting = (on: boolean) => setPosting(on);

  /**
   * The block opens four fields below the fold on a short window, and the
   * modal's body is what scrolls — so revealing them is not the same as
   * showing them. Bringing the last one into view brings the whole block.
   */
  const lastPostingField = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!posting) return;
    lastPostingField.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [posting]);

  const setPosted = (field: keyof Posting, value: string) => {
    setTemp((current) => ({ ...current, [field]: value }));
    // Which end of a crossed pair to complain about: the one just moved, so
    // the message is about what was done rather than about the other date.
    if (field === "temporary_start_date" || field === "temporary_end_date") setCrossedOn(field);
  };

  /**
   * The two fixed choices, plus whatever an older record was filed under —
   * a reason already on the account is never dropped just because it is not
   * one of the two.
   */
  const reasonChoices = useMemo(() => {
    const choices = [...CLUB_CHANGE_REASONS];
    const current = temp.temporary_club_reason;
    return current && !choices.some((choice) => choice.value === current)
      ? [...choices, { value: current, label: current }]
      : choices;
  }, [temp.temporary_club_reason]);

  const save = useMutation({
    mutationFn: async (parts: {
      first: string;
      middle: string | null;
      last: string | null;
    }) => {
      const body = {
        first_name: parts.first,
        middle_name: parts.middle,
        last_name: parts.last,
        // Hidden is not the same as absent: see `emailToSend`.
        email_address: emailToSend(),
        manager_id: showManager && values.manager_id ? Number(values.manager_id) : null,
        club_id: values.club_id ? Number(values.club_id) : null,
        group_ids: values.group_id ? [Number(values.group_id)] : [],
      };
      const saved = creating
        ? (
            await api.post<Employee>("/users", {
              ...body,
              user_name: values.user_name.trim(),
              password: values.password,
              force_password_change: true,
            })
          ).data
        : (
            await api.patch<Employee>(
              `/users/${employee.id}`,
              {
                ...body,
                // The posting rides along: set it, end it, or leave it untouched.
                ...(posting
                  ? {
                      temporary_club: {
                        temporary_club_id: Number(temp.temporary_club_id),
                        temporary_club_reason: temp.temporary_club_reason,
                        temporary_start_date: temp.temporary_start_date,
                        temporary_end_date: temp.temporary_end_date,
                      },
                    }
                  : employee.is_temporary_club
                    ? { temporary_club: null }
                    : {}),
              },
              // Optimistic locking: the row's `updated_at` as last read. Without
              // it the edit is refused with 428.
              ifMatch(employee),
            )
          ).data;
      return saved;
    },
    onSuccess: () => {
      toast.success(creating ? t("emp.created") : t("emp.updated"));
      onSaved();
    },
    onError: (err) => setError(errorMessage(err, t("emp.saveFailed"))),
  });

  /**
   * Every field, read off the values as they stand.
   *
   * The browser judges what it can — see `Form` — but almost nothing here is
   * its kind of question: the temporary password is read-only, which bars a
   * control from native validation entirely; a picker left empty means "choose
   * one from the list" rather than "fill this in"; and a start date is only
   * wrong in the company of an end date. So the rules are written out, keyed
   * by the id each field's label points at, and `Form` decides when each one
   * is allowed to speak — on leaving the field, or on Submit.
   *
   * Pure, and called on every read: nothing is stored, so no message can
   * outlive what it is about.
   */
  /**
   * A crossed pair of dates says so the moment it is crossed.
   *
   * Emptiness waits to be left — someone halfway through a field has not
   * failed to fill it in yet — but two dates in the wrong order are only wrong
   * in each other's company, and that company arrives with the second one. It
   * is named on the date last moved, so the message is about what was just
   * done rather than about the other end of the pair.
   */
  const crossedAt = !creating && posting && crossed(temp) ? crossedOn : null;
  const crossedMessage = crossedAt
    ? t(crossedAt === "temporary_start_date" ? "emp.startAfterEnd" : "emp.endBeforeStart")
    : null;

  const rules = (): Errors => {
    const found: Errors = {};
    if (!values.user_name.trim()) found.user_name = t("emp.fieldRequired");
    if (!values.name.trim()) found.name = t("emp.fieldRequired");
    if (!values.group_id) found.group_id = t("emp.selectOption");
    if (!values.club_id) found.club_id = t("emp.selectOption");
    if (creating && !values.password) found.password = t("emp.passwordRequired");
    if (showManager && !values.manager_id) found.manager_id = t("emp.selectOption");
    if (showEmail) {
      if (!values.email_address.trim()) found.email_address = t("emp.fieldRequired");
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email_address.trim()))
        found.email_address = t("emp.emailInvalid");
    }
    if (!creating && posting) {
      if (!temp.temporary_club_id) found.temporary_club_id = t("emp.selectOption");
      if (!temp.temporary_club_reason.trim())
        found.temporary_club_reason = t("emp.fieldRequired");
      if (!temp.temporary_start_date) found.temporary_start_date = t("emp.fieldRequired");
      if (!temp.temporary_end_date) found.temporary_end_date = t("emp.fieldRequired");
      if (crossedAt && crossedMessage) found[crossedAt] = crossedMessage;
    }
    return found;
  };

  // A number already on the table is not something a retry fixes, so Submit
  // stays out of reach until the number changes.
  const blocked = duplicate !== null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    // `Form` has already held the save back over anything `rules` refused, so
    // what is left is the name, which is split rather than judged: one word is
    // a name like any other.
    if (blocked) return;
    const parts = splitName(values.name);
    if (parts) save.mutate(parts);
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      /* Six rows once the posting block is open, which the shared 44rem cap
         cuts off at the dates — the last row. Taller here, and the body still
         scrolls on a short screen. */
      className="max-h-[min(92dvh,56rem)]"
      title={creating ? t("emp.create") : t("emp.edit")}
      footer={
        // The way out beside the way on, like the rest of the form sheets: the
        // X in the corner is the only other way to leave a form this long, and
        // it is the furthest thing from the thumb. Half the width each on a
        // phone, where the sheet is the screen; their own size against the
        // right edge from `sm` up, where it is a centred dialog again.
        <div className="grid w-full grid-cols-2 gap-2.5 sm:flex sm:w-auto sm:gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={blocked}
            loading={save.isPending}
            loadingLabel={t("new.saving")}
          >
            {t("common.submit")}
          </Button>
        </div>
      }
    >
      {error ? (
        <p
          role="alert"
          className="bg-danger-soft text-danger text-callout mb-4 flex items-start gap-2 rounded-lg px-3.5 py-2.5"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : null}

      <Form
        id={FORM_ID}
        validate={rules}
        onSubmit={submit}
        className="grid gap-4 pb-2 md:grid-cols-2 md:items-start"
      >
        {/* A number already on the table is the server's answer rather than a
            rule of the form's, so this field keeps that one message for
            itself; everything else under it comes from `rules`. */}
        <Field
          label={t("emp.employeeNumber")}
          required
          htmlFor="user_name"
          {...(duplicate
            ? { error: duplicate }
            : lookup.busy || lookup.note
              ? { hint: lookup.busy ? t("emp.lookingUp") : lookup.note! }
              : {})}
        >
          <Input
            id="user_name"
            disabled={!creating}
            autoComplete="off"
            aria-invalid={duplicate ? true : undefined}
            value={values.user_name}
            onChange={(e) => changeNumber(e.target.value)}
            placeholder={t("emp.enterEmployeeNumber")}
          />
        </Field>

        <Field label={t("emp.name")} required htmlFor="name">
          {/* Held to what the directory said, until the number changes. */}
          <Input
            id="name"
            readOnly={nameLocked}
            value={values.name}
            {...(nameLocked ? { className: "bg-surface-inset cursor-not-allowed" } : {})}
            onChange={(e) => set("name", e.target.value)}
            placeholder={t("emp.enterName")}
          />
        </Field>

        <Field label={t("emp.role")} required htmlFor="group_id">
          <FkSelect
            id="group_id"
            endpoint="/groups/compact"
            value={values.group_id}
            onChange={(next, label) => {
              set("group_id", next);
              setRoleName(label);
            }}
            placeholder={t("emp.selectRole")}
          />
        </Field>

        <Field label={t("emp.club")} required htmlFor="club_id">
          <FkSelect
            id="club_id"
            endpoint="/clubs/compact"
            value={values.club_id}
            onChange={(next) => set("club_id", next)}
            placeholder={t("emp.selectClub")}
          />
        </Field>

        {/* A row of its own, as the design has it. */}
        {creating ? (
          <Field
            className="md:col-start-1"
            label={
              <span className="inline-flex items-center gap-2">
                {t("emp.temporaryPassword")}
                <button
                  type="button"
                  onClick={() => set("password", generatePassword())}
                  className="border-line bg-surface-inset text-fg text-footnote inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-medium"
                >
                  <RefreshCw className="size-3" aria-hidden /> {t("emp.generate")}
                </button>
              </span>
            }
            required
            htmlFor="password"
            hint={t("emp.passwordHint")}
          >
            {/* The one field nobody fills in: only Generate writes to it, and
                it wears the disabled look — flat fill, no border, and a cursor
                that refuses — so the box never invites a click of its own.
                Read-only rather than truly disabled, because the password
                still has to be selectable to be passed on. */}
            <Input
              id="password"
              readOnly
              value={values.password}
              placeholder={t("emp.clickToGenerate")}
              className="bg-surface-disabled text-fg-subtle [&:is(input,textarea):read-only]:bg-surface-disabled cursor-not-allowed border-transparent font-mono"
            />
          </Field>
        ) : null}

        {/* Whichever of the two the role calls for opens the last row, and on
            an edit the posting's checkbox sits beside it in that same row —
            the two things left to decide about this account, side by side. */}
        {showManager ? (
          <Field
            className="md:col-start-1"
            label={t("emp.manager")}
            required
            htmlFor="manager_id"
          >
            <FkSelect
              id="manager_id"
              endpoint="/users/compact"
              // Supervisors only. The field exists because an associate is
              // reached through one — their password goes to the supervisor's
              // address — so every other account in the estate is a wrong
              // answer to scroll past.
              params={{ role: SUPERVISOR_ROLE }}
              value={values.manager_id}
              onChange={(next) => set("manager_id", next)}
              exclude={(option) => option.id === employee?.id}
              placeholder={t("emp.selectManager")}
            />
          </Field>
        ) : null}

        {showEmail ? (
          <Field
            className="md:col-start-1"
            label={t("emp.emailAddress")}
            required
            htmlFor="email_address"
          >
            <Input
              id="email_address"
              type="email"
              value={values.email_address}
              onChange={(e) => set("email_address", e.target.value)}
            />
          </Field>
        ) : null}

        {/* A posting is something that happens to an account that already
            exists, so it is an edit's business and never appears on Add. */}
        {creating ? null : (
          <label className="text-callout flex items-start gap-3 pt-7 md:col-start-2">
            <input
              type="checkbox"
              className="accent-accent mt-0.5 size-5"
              checked={posting}
              onChange={(e) => togglePosting(e.target.checked)}
            />
            <span className="text-fg font-semibold">{t("emp.addTemporaryClub")}</span>
          </label>
        )}

        {!creating && posting ? (
          <>
            <Field label={t("emp.temporaryClub")} required htmlFor="temporary_club_id">
              <FkSelect
                id="temporary_club_id"
                endpoint="/clubs/compact"
                value={temp.temporary_club_id}
                onChange={(next) => setPosted("temporary_club_id", next)}
                exclude={(option) => String(option.id) === values.club_id}
                placeholder={t("emp.selectClub")}
              />
            </Field>
            <Field label={t("emp.reason")} required htmlFor="temporary_club_reason">
              {/* The configured list and nothing else — no free text, and no
                  making one from here. Drawn by us rather than by the browser:
                  a native <select> opens the operating system's own menu,
                  which is grey-on-black on macOS and nothing like the pickers
                  either side of it. The value is the reason as written,
                  because that is what the account stores: editing the list
                  later does not rewrite what a posting was filed under. */}
              <OptionSelect
                id="temporary_club_reason"
                value={temp.temporary_club_reason}
                choices={reasonChoices}
                placeholder={t("emp.selectReason")}
                onChange={(next) => setPosted("temporary_club_reason", next)}
              />
            </Field>
            {/* The app's calendar, not the operating system's — the same one
                the enrolment form and the member screens ask dates with. A
                native `type="date"` is a different control on every platform,
                asks for the date in whatever order the browser's locale
                writes it, and paints itself in its own blue beside the two
                pickers above it. What it hands back is unchanged: both ends
                of the posting are still plain `YYYY-MM-DD`, which is what the
                crossed-pair check reads and what the API is sent. */}
            <Field
              label={t("emp.startDate")}
              required
              htmlFor="temporary_start_date"
              {...(crossedAt === "temporary_start_date" && crossedMessage
                ? { error: crossedMessage }
                : {})}
            >
              <DatePicker
                id="temporary_start_date"
                invalid={crossedAt === "temporary_start_date"}
                value={temp.temporary_start_date}
                onChange={(next) => setPosted("temporary_start_date", next)}
              />
            </Field>
            <Field
              label={t("emp.endDate")}
              required
              htmlFor="temporary_end_date"
              {...(crossedAt === "temporary_end_date" && crossedMessage
                ? { error: crossedMessage }
                : {})}
            >
              {/* The wrapper is only somewhere to hold on to: the block's last
                  field is what gets scrolled to, and a picker drawn by antd
                  has no ref of its own to offer. */}
              <div ref={lastPostingField}>
                <DatePicker
                  id="temporary_end_date"
                  invalid={crossedAt === "temporary_end_date"}
                  value={temp.temporary_end_date}
                  onChange={(next) => setPosted("temporary_end_date", next)}
                />
              </div>
            </Field>
          </>
        ) : null}
      </Form>
    </Modal>
  );
}
